import { MouseEvent } from 'react';

import { useCancelLimitOrder } from '@/hooks/useCancelLimitOrder';

import { linkStyles } from '@/styles/links';

import { LoadingSpinner } from '@/ui/Icons/Animated/LoadingSpinner';
import { TrashIcon } from '@/ui/Icons/TrashIcon';

import type { LimitOrderItem } from '@/utils/0x.types';

interface CancelOrderButtonProps {
  /** The open limit order to cancel. */
  item: LimitOrderItem;
}

/**
 * Cancels a single open limit order on-chain. Both the trade page and history
 * open-order tables read from the `limit-orders` React Query cache, so the cache
 * invalidation inside `useCancelLimitOrder` is what removes the row on success.
 */
export function CancelOrderButton({ item }: CancelOrderButtonProps) {
  const { isCancelling, cancelOrder } = useCancelLimitOrder(item);

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    // prevent the details modal / row navigation from triggering
    event.stopPropagation();
    cancelOrder();
  };

  return (
    <button
      aria-label="Cancel order"
      disabled={isCancelling}
      onClick={handleCancel}
      className={linkStyles({
        class: 'inline-flex items-center gap-1 align-middle text-token-red-900',
      })}
    >
      {isCancelling ? (
        <LoadingSpinner className="h-4 w-4" />
      ) : (
        <TrashIcon className="h-4 w-4" name="Cancel order" />
      )}
    </button>
  );
}
